import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import CurrencySwitcher from '../shared/CurrencySwitcher';

/**
 * MobileCurrencyMenu — compact currency picker for the mobile top bars.
 *
 * Mirrors the desktop navbar's currency dropdown, which is `hidden md:block`
 * and so never reached on phones. Lives in MobileHomeHeader and MobileBackNav
 * so buyers can always switch their display currency where prices are shown.
 */
const MobileCurrencyMenu: React.FC = () => {
  const { currency, meta } = useCurrencyFormat();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        className="h-8 pl-2 pr-1.5 rounded-full bg-[#e9b306]/10 text-[#c99a00] active:scale-95 border border-[#e9b306]/20 inline-flex items-center gap-1 transition-all text-[12px] font-bold"
        aria-label={`Currency: ${currency}. Tap to change`}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <span className="inline-flex items-center justify-center w-3.5 text-center">
          {meta.symbol}
        </span>
        <span className="tracking-wide">{currency}</span>
        <ChevronDown size={13} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-xl shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 z-50">
          <CurrencySwitcher onChange={() => setIsOpen(false)} />
        </div>
      )}
    </div>
  );
};

export default MobileCurrencyMenu;
